import React, { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { kpiMode, useSourceView } from './UseSourceView.ts'
import { visualizationSlice } from '../../../../Flux/Slice/Visualization/VisualizationSlice.js'
import type { SinkConfig, SinkId, SourceId } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { isLoadingState } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { TreemapChartNext, type TreemapNode } from '../../../Component/Charts/TreemapChartNext.tsx'
import { useTransformer } from '../Transformer/UseTransformer.tsx'
import { useBreakdownSinkTable } from '../DataTable/UseSinkTable.ts'

type TreemapParams = { title?: string }

const EMPTY_PATH: string[] = []

export default function TreemapSink({
  sourceId,
  bucketKey
}: {
  sourceId: SourceId
  sinkId: SinkId
  config: SinkConfig<TreemapParams>
  bucketKey?: string
}) {
  const dispatch = useDispatch()

  const { snapshot, shared, status } = useSourceView(sourceId)

  const selectedPath = shared?.selectedPath ?? EMPTY_PATH
  const gradeThreshold = shared?.gradeThreshold ?? 0
  const scalingMode = shared?.scalingMode ?? 'ABSOLUTE'

  // Nature-KPI mode: compose by trait group instead of taxon. `traitAggregationSingle` returns the
  // same shape as `taxaAggregationSingle`, so the render below is mode-agnostic.
  const { inTraitMode, traitGroup, noTraitMode } = kpiMode(shared)

  // Single-bucket aggregation (bucketKey passed as hook arg)
  const aggRes = inTraitMode
    ? useTransformer(sourceId, bucketKey, 'traitAggregationSingle', { selectedPath, gradeThreshold, traitGroup, scalingMode, noTraitMode })
    : useTransformer(sourceId, bucketKey, 'taxaAggregationSingle', { selectedPath, gradeThreshold })

  const data = useMemo(() => {
    if (aggRes.status !== 'ready') return [] as TreemapNode[]

    const { taxa, rows } = aggRes.value

    // Aggregate totals across rows (single bucket = typically one row, but keep generic)
    const totals: Record<string, number> = Object.fromEntries(taxa.map((t) => [t, 0]))
    for (const r of rows) {
      for (const [k, v] of Object.entries(r.data)) {
        totals[k] = (totals[k] ?? 0) + (v ?? 0)
      }
    }

    return Object.entries(totals)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name, value }))
  }, [aggRes])

  // The slices as numbers, published to the container's "Numbers" view. Same entries the chart
  // draws, so the table cannot drift from it.
  useBreakdownSinkTable({
    sourceId,
    bucketKey,
    selectedPath,
    gradeThreshold,
    inTraitMode,
    ready: aggRes.status === 'ready' && data.length > 0,
    entries: data,
    withTaxaCounts: !inTraitMode,
    categoryLabel: inTraitMode ? 'Trait group' : 'Taxon',
    valueLabel: scalingMode === 'PERCENTAGE' ? 'Share (%)' : String(shared?.countMode ?? 'Reads')
  })

  const onNodeClick = (node: { name?: string }) => {
    if (inTraitMode) return // trait groups aren't a drillable taxonomy level
    const name: string | undefined = node?.name
    if (!name || name === 'root') return
    const nextPath = [...selectedPath, name]
    dispatch(visualizationSlice.actions.setTaxaZoom({ sourceId, path: nextPath }))
  }

  if (!snapshot) {
    return (
      <div className="text-sm opacity-70">
        Waiting for data… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}
      </div>
    )
  }

  if (aggRes.status === 'idle' || aggRes.status === 'loading') {
    return (
      <div className="text-sm opacity-70">Computing… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}</div>
    )
  }

  if (aggRes.status === 'error') {
    return <div className="text-sm opacity-70">Error computing treemap.</div>
  }

  if (data.length === 0) {
    return <div className="text-sm opacity-70">No data for current selection.</div>
  }

  return (
    <div className="flex flex-col gap-2 h-full">
      <div className="text-xs opacity-70">Treemap: {inTraitMode ? 'Trait composition' : 'Taxa composition'}</div>
      <div className="w-full h-full">
        <TreemapChartNext data={data} onNodeClick={(name) => onNodeClick({ name })} height="100%" />
      </div>
    </div>
  )
}
